import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Card, CategoryChip, ChangeText, EmptyState, ImpactBadge, LiveNumber, Skeleton } from '../ui'
import { api } from '../lib/api'
import { formatNumber, formatTimeIST } from '../lib/format'
import { useLiveStocks, useLiveSymbols } from '../lib/liveMarket'
import type { LiveQuote } from '../lib/liveMarket'
import { usePageTitle } from '../lib/usePageTitle'
import { RequireAuth } from '../app/RequireAuth'
import styles from './RankedPage.module.css'

interface Levels { direction: string; setup: string | null; entry_low: number | null; entry_high: number | null; stop_loss: number | null; target_1: number | null; target_2: number | null; risk_reward: number | null; no_trade_reason: string | null; chartink_note: string | null }
interface Row {
  id: number; rank: number | null; symbol: string; name: string | null; sector: string | null; score: number; grade: string | null
  tone: 'positive' | 'negative' | 'neutral'; category: string | null; headline: string | null; summary: string | null
  filed_at: string | null; ranked_at: string; document_url: string | null; chartink_confirmed: boolean | null
  ranked_price: number | null; actionable: boolean; levels?: Levels
}
interface Explain extends Row { components: Array<{ label: string; points: number | null; max: number }>; timing: string | null; analysis: string | null; materiality: string | null; urgency: string | null; confidence: number | null }
interface ListResp { date: string; levels_visible: boolean; data: Row[] }

const DISCLAIMER = 'For personal study only — not investment advice.'
const TONE = { positive: { word: 'Positive event', arrow: '▲' }, negative: { word: 'Negative event', arrow: '▼' }, neutral: { word: 'Mixed / unclear', arrow: '■' } } as const

type Status = { key: 'avail' | 'wait' | 'late' | 'stopped' | 'missed' | 'none'; label: string; sub: string }
/** Same rules as the old Ranked tab: where is the live price relative to the entry zone, stop-loss and target 1? */
export function tradeStatus(l: Levels | undefined, ltp: number | null | undefined): Status {
  if (!l || !ltp || !l.entry_low) return { key: 'none', label: '—', sub: '' }
  const lo = l.entry_low, hi = l.entry_high || l.entry_low, sl = l.stop_loss, t1 = l.target_1
  const r = (v: number) => formatNumber(Math.abs(v), 0)
  if (l.direction === 'long') {
    if (t1 && ltp >= t1) return { key: 'missed', label: 'MISSED', sub: `T1 ₹${r(t1)} hit` }
    if (sl && ltp <= sl) return { key: 'stopped', label: 'STOPPED', sub: 'below SL' }
    if (ltp >= lo && ltp <= hi) return { key: 'avail', label: 'AVAILABLE', sub: 'in entry zone' }
    if (ltp > hi) return { key: 'late', label: 'LATE', sub: `+₹${r(ltp - hi)} past entry` }
    return { key: 'wait', label: 'WAIT', sub: `needs +₹${r(lo - ltp)}` }
  }
  if (l.direction === 'short') {
    if (t1 && ltp <= t1) return { key: 'missed', label: 'MISSED', sub: `T1 ₹${r(t1)} hit` }
    if (sl && ltp >= sl) return { key: 'stopped', label: 'STOPPED', sub: 'above SL' }
    if (ltp >= lo && ltp <= hi) return { key: 'avail', label: 'AVAILABLE', sub: 'in entry zone' }
    if (ltp < lo) return { key: 'late', label: 'LATE', sub: `-₹${r(lo - ltp)} past entry` }
    return { key: 'wait', label: 'WAIT', sub: `needs -₹${r(ltp - hi)}` }
  }
  return { key: 'none', label: '—', sub: '' }
}

const istToday = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10)
const dayLabel = (d: string) => d === istToday() ? 'Today' : new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
const pctSince = (ltp: number | null | undefined, base: number | null) => (ltp && base ? ((ltp - base) / base) * 100 : null)

export function RankedPage() {
  usePageTitle('Ranked')
  return <RequireAuth what="Ranked"><Ranked /></RequireAuth>
}

function Ranked() {
  const [params, setParams] = useSearchParams()
  const [date, setDate] = useState(istToday())
  const [min, setMin] = useState(65)
  const [toneFilter, setToneFilter] = useState<'all' | 'positive' | 'negative'>('all')
  const [view, setView] = useState<'actionable' | 'all'>('actionable')
  const [data, setData] = useState<ListResp | null>(null)
  const [days, setDays] = useState<Array<{ day: string; n: number }>>([])
  const [error, setError] = useState<string | null>(null)
  const openId = Number(params.get('id')) || null
  const live = useLiveStocks()

  useEffect(() => { api<{ data: Array<{ day: string; n: number }> }>('/ranked/days').then(r => setDays(r.data)).catch(() => undefined) }, [])
  useEffect(() => {
    let alive = true
    const load = () => api<ListResp>(`/ranked?date=${date}&min=${min}&view=${view}`).then(r => { if (alive) { setData(r); setError(null) } }).catch(e => { if (alive) setError((e as Error).message) })
    setData(null); void load()
    // New ideas arrive through the day: refresh the list every minute (prices tick live meanwhile).
    const t = date === istToday() ? setInterval(() => { if (document.visibilityState === 'visible') void load() }, 60000) : undefined
    return () => { alive = false; if (t) clearInterval(t) }
  }, [date, min, view])

  const rows = useMemo(() => (data?.data ?? []).filter(r => toneFilter === 'all' || r.tone === toneFilter), [data, toneFilter])
  useLiveSymbols(rows.slice(0, 20).map(r => r.symbol))
  const full = !!data?.levels_visible
  const counts = useMemo(() => {
    const all = data?.data ?? []
    return { all: all.length, positive: all.filter(r => r.tone === 'positive').length, negative: all.filter(r => r.tone === 'negative').length }
  }, [data])
  const dayOptions = days.some(d => d.day === istToday()) ? days : [{ day: istToday(), n: 0 }, ...days]

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.h1}>Ranked</h1>
          <p className={styles.sub}>{full ? 'Scanner ideas scoring 65+, one per stock, best first — with levels and live status.' : 'Today’s highest-scoring corporate events (score 65+), one per stock, best first — with the live market reaction.'}</p>
        </div>
        <div className={styles.controls}>
          <label className="sr-only" htmlFor="rk-day">Day</label>
          <select id="rk-day" className={styles.select} value={date} onChange={e => setDate(e.target.value)}>
            {dayOptions.map(d => <option key={d.day} value={d.day}>{dayLabel(d.day)}{d.n ? ` · ${d.n}` : ''}</option>)}
          </select>
          <div className={styles.seg} role="group" aria-label="Minimum score">
            {[65, 70, 80].map(v => <button key={v} type="button" aria-pressed={min === v} className={min === v ? styles.on : undefined} onClick={() => setMin(v)}>{v}+</button>)}
          </div>
          {full && (
            <div className={styles.seg} role="group" aria-label="Which ideas">
              <button type="button" aria-pressed={view === 'actionable'} className={view === 'actionable' ? styles.on : undefined} onClick={() => setView('actionable')}>Trade ideas</button>
              <button type="button" aria-pressed={view === 'all'} className={view === 'all' ? styles.on : undefined} onClick={() => setView('all')}>All ranked</button>
            </div>
          )}
        </div>
      </header>

      {full && <p className={styles.banner} role="note"><b>Full view · invite-only.</b> {DISCLAIMER} Levels are the scanner’s; check the chart and your own risk before acting.</p>}

      <div className={styles.tabs} role="group" aria-label="Filter by event tone">
        {(['all', 'positive', 'negative'] as const).map(t => (
          <button key={t} type="button" aria-pressed={toneFilter === t} className={`${styles.tab} ${toneFilter === t ? styles.tabOn : ''}`} onClick={() => setToneFilter(t)}>
            {t === 'all' ? 'All' : t === 'positive' ? '▲ Positive' : '▼ Negative'}<span className={styles.count}>{counts[t]}</span>
          </button>
        ))}
      </div>

      <Card variant="flush">
        {error ? <EmptyState kind="error" title="Could not load Ranked" compact>{error}</EmptyState>
          : !data ? <div className={styles.pad}><Skeleton variant="row" lines={6} label="Loading Ranked" /></div>
          : rows.length === 0 ? <EmptyState title={date === istToday() ? 'Nothing ranked yet today' : 'Nothing ranked on this day'} compact>Ideas appear here as filings are scored during the day.</EmptyState>
          : <MobileCards rows={rows} live={live} full={full} today={date === istToday()} onWhy={id => setParams(p => { p.set('id', String(id)); return p })} />}
        {data && rows.length > 0 && (full ? <FullTable today={date === istToday()} rows={rows} live={live} onWhy={id => setParams(p => { p.set('id', String(id)); return p })} />
          : <CompliantTable rows={rows} live={live} onWhy={id => setParams(p => { p.set('id', String(id)); return p })} />)}
      </Card>
      <p className={styles.fine}>{full ? DISCLAIMER : 'Information on corporate filings only. Not a recommendation to buy or sell.'} Scores come from event materiality, historical reaction, technicals, volume, sector and data quality.</p>

      {openId && <WhyPanel id={openId} live={live} onClose={() => setParams(p => { p.delete('id'); return p })} />}
    </div>
  )
}

/** Phones: one card per idea instead of a wide table. */
function MobileCards({ rows, live, full, today, onWhy }: { rows: Row[]; live: (s: string) => LiveQuote | undefined; full: boolean; today: boolean; onWhy: (id: number) => void }) {
  const f = (v: number | null | undefined) => (v == null ? '—' : formatNumber(v))
  return (
    <ul className={styles.cards}>
      {rows.map((r, i) => {
        const q = live(r.symbol); const L = r.levels
        const st: Status = today ? tradeStatus(L, q?.ltp) : { key: 'none', label: '', sub: '' }
        return (
          <li key={r.id} className={styles.card}>
            <div className={styles.cardTop}>
              <span className={styles.rank}>{i + 1}</span>
              <StockCell r={r} />
              <span className={styles.scoreCell}><ImpactBadge score={Math.round(r.score)} size="sm" />{r.grade && <span className={styles.grade}>{r.grade}</span>}</span>
            </div>
            <div className={styles.cardRow}>
              {full && L ? <span className={`${styles.dir} ${styles[L.direction === 'long' ? 'long' : L.direction === 'short' ? 'short' : 'watch']}`}>{L.direction === 'long' ? '▲ LONG' : L.direction === 'short' ? '▼ SHORT' : 'WATCH'}</span>
                : <span className={`${styles.tone} ${styles[r.tone]}`}>{TONE[r.tone].arrow} {TONE[r.tone].word}</span>}
              <span className={styles.cardPx}><LiveNumber value={q?.ltp ?? null} /> <ChangeText percent={q?.change_pct ?? null} size="sm" /></span>
            </div>
            {full && L?.entry_low ? (
              <dl className={styles.cardLevels}>
                <div><dt>Entry</dt><dd>{f(L.entry_low)}{L.entry_high && L.entry_high !== L.entry_low ? `–${f(L.entry_high)}` : ''}</dd></div>
                <div><dt>SL</dt><dd className={styles.sl}>{f(L.stop_loss)}</dd></div>
                <div><dt>T1</dt><dd>{f(L.target_1)}</dd></div>
                <div><dt>T2</dt><dd>{f(L.target_2)}</dd></div>
              </dl>
            ) : <p className={styles.cardHead}>{r.category && <CategoryChip category={r.category} size="sm" />} {r.headline}</p>}
            <div className={styles.cardRow}>
              {st.key !== 'none' ? <span><span className={`${styles.status} ${styles[`s_${st.key}`]}`}>{st.label}</span> <span className={styles.metaInline}>{st.sub}</span></span> : <span className={styles.metaInline}>{L?.setup ?? (r.filed_at ? `Filed ${formatTimeIST(r.filed_at)}` : '')}</span>}
              <button type="button" className={styles.why} onClick={() => onWhy(r.id)}>Why?</button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function StockCell({ r }: { r: Row }) {
  return (
    <span className={styles.stock}>
      <Link to={`/stock/${encodeURIComponent(r.symbol)}`} className={styles.sym}>{r.symbol}</Link>
      <span className={styles.name}>{r.name ?? ''}</span>
    </span>
  )
}

function CompliantTable({ rows, live, onWhy }: { rows: Row[]; live: (s: string) => LiveQuote | undefined; onWhy: (id: number) => void }) {
  return (
    <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Ranked events (scrolls sideways)">
      <table className={styles.table}>
        <caption className="sr-only">Ranked corporate events</caption>
        <thead><tr><th scope="col">#</th><th scope="col">Stock</th><th scope="col">Score</th><th scope="col">Event</th><th scope="col">Tone</th>
          <th scope="col" className={styles.num}>LTP</th><th scope="col" className={styles.num}>Today</th><th scope="col" className={styles.num}>Since ranked</th><th scope="col"><span className="sr-only">Details</span></th></tr></thead>
        <tbody>{rows.map((r, i) => {
          const q = live(r.symbol)
          return (
            <tr key={r.id}>
              <td className={styles.rank}>{i + 1}</td>
              <td><StockCell r={r} /></td>
              <td><span className={styles.scoreCell}><ImpactBadge score={Math.round(r.score)} size="sm" />{r.grade && <span className={styles.grade}>{r.grade}</span>}</span></td>
              <td className={styles.event}>{r.category && <CategoryChip category={r.category} size="sm" />}<span className={styles.headline}>{r.headline ?? r.summary ?? '—'}</span>
                <span className={styles.meta}>{r.filed_at ? `Filed ${formatTimeIST(r.filed_at)}` : ''}{r.chartink_confirmed ? ' · 📊 Chartink' : ''}</span></td>
              <td><span className={`${styles.tone} ${styles[r.tone]}`}>{TONE[r.tone].arrow} {TONE[r.tone].word}</span></td>
              <td className={styles.num}><LiveNumber value={q?.ltp ?? null} /></td>
              <td className={styles.num}><ChangeText percent={q?.change_pct ?? null} size="sm" /></td>
              <td className={styles.num}><ChangeText percent={pctSince(q?.ltp, r.ranked_price)} size="sm" /></td>
              <td><button type="button" className={styles.why} onClick={() => onWhy(r.id)}>Why?</button></td>
            </tr>
          )
        })}</tbody>
      </table>
    </div>
  )
}

function FullTable({ rows, live, onWhy, today }: { rows: Row[]; live: (s: string) => LiveQuote | undefined; onWhy: (id: number) => void; today: boolean }) {
  const f = (v: number | null | undefined) => (v == null ? '—' : formatNumber(v))
  return (
    <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Ranked trade ideas (scrolls sideways)">
      <table className={styles.table}>
        <caption className="sr-only">Ranked trade ideas with levels</caption>
        <thead><tr><th scope="col">#</th><th scope="col">Stock</th><th scope="col">Score</th><th scope="col">Signal</th>
          <th scope="col" className={styles.num}>Entry</th><th scope="col" className={styles.num}>SL</th><th scope="col" className={styles.num}>T1 / T2</th><th scope="col" className={styles.num}>R:R</th>
          <th scope="col" className={styles.num}>LTP</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Details</span></th></tr></thead>
        <tbody>{rows.map((r, i) => {
          const q = live(r.symbol); const L = r.levels
          // Live status only makes sense for today's ideas; older ones are judged in the admin track record.
          const st: Status = today ? tradeStatus(L, q?.ltp) : { key: 'none', label: 'Past day', sub: 'status is for today only' }
          const dir = L?.direction === 'long' ? 'long' : L?.direction === 'short' ? 'short' : null
          return (
            <tr key={r.id}>
              <td className={styles.rank}>{i + 1}</td>
              <td><StockCell r={r} />{r.category && <span className={styles.meta}><CategoryChip category={r.category} size="sm" /></span>}</td>
              <td><span className={styles.scoreCell}><ImpactBadge score={Math.round(r.score)} size="sm" />{r.grade && <span className={styles.grade}>{r.grade}</span>}</span></td>
              <td>{dir ? <span className={`${styles.dir} ${styles[dir]}`}>{dir === 'long' ? '▲ LONG' : '▼ SHORT'}</span> : <span className={`${styles.dir} ${styles.watch}`}>WATCH</span>}
                <span className={styles.meta}>{L?.setup ?? ''}{r.chartink_confirmed ? ' · 📊' : ''}</span></td>
              <td className={styles.num}>{L?.entry_low ? (L.entry_high && L.entry_high !== L.entry_low ? `${f(L.entry_low)}–${f(L.entry_high)}` : f(L.entry_low)) : '—'}</td>
              <td className={`${styles.num} ${styles.sl}`}>{f(L?.stop_loss)}</td>
              <td className={styles.num}>{f(L?.target_1)}{L?.target_2 ? <span className={styles.meta}>{f(L.target_2)}</span> : null}</td>
              <td className={styles.num}>{L?.risk_reward ? `1:${formatNumber(L.risk_reward, 1)}` : '—'}</td>
              <td className={styles.num}><LiveNumber value={q?.ltp ?? null} /><span className={styles.meta}><ChangeText percent={q?.change_pct ?? null} size="sm" /></span></td>
              <td><span className={`${styles.status} ${styles[`s_${st.key}`]}`}>{st.label}</span><span className={styles.meta}>{st.sub}</span></td>
              <td><button type="button" className={styles.why} onClick={() => onWhy(r.id)}>Why?</button></td>
            </tr>
          )
        })}</tbody>
      </table>
    </div>
  )
}

function WhyPanel({ id, live, onClose }: { id: number; live: (s: string) => LiveQuote | undefined; onClose: () => void }) {
  const [d, setD] = useState<Explain | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { setD(null); api<{ data: Explain }>(`/ranked/${id}`).then(r => setD(r.data)).catch(e => setErr((e as Error).message)) }, [id])
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k) }, [onClose])
  const q = d ? live(d.symbol) : undefined
  const L = d?.levels
  const isToday = d ? new Date(new Date(d.ranked_at).getTime() + 5.5 * 3600e3).toISOString().slice(0, 10) === istToday() : false
  const st: Status = isToday ? tradeStatus(L, q?.ltp) : { key: 'none', label: '', sub: '' }
  return (
    <div className={styles.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <aside className={styles.panel} role="dialog" aria-modal="true" aria-label={d ? `Why ${d.symbol} is ranked` : 'Why ranked'}>
        <div className={styles.panelHead}>
          <div>{d ? <><h2 className={styles.h2}>{d.symbol} <span className={styles.name}>{d.name}</span></h2>
            <p className={styles.meta}>Ranked {formatTimeIST(d.ranked_at)} · score {d.score}{d.grade ? ` (${d.grade})` : ''}</p></> : <h2 className={styles.h2}>Why ranked</h2>}</div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>
        {err ? <EmptyState kind="error" title="Could not load" compact>{err}</EmptyState> : !d ? <Skeleton lines={6} label="Loading" /> : (
          <div className={styles.panelBody}>
            <div className={styles.quote}>
              {q?.ltp ? <><span><LiveNumber value={q.ltp} prefix="₹" className={styles.bigPx} /></span><ChangeText percent={q.change_pct ?? null} size="md" /></>
                : <span className={styles.meta}>No live price for {d.symbol} (not on the NSE live feed — SME / BSE-only)</span>}
              {d.ranked_price ? <span className={styles.meta}>since ranked <ChangeText percent={pctSince(q?.ltp, d.ranked_price)} size="sm" /></span> : null}
              <span className={`${styles.tone} ${styles[d.tone]}`}>{TONE[d.tone].arrow} {TONE[d.tone].word}</span>
            </div>

            {L && (
              <section className={styles.levelsBox} aria-label="Levels">
                <div className={styles.levelsHead}>
                  <span className={`${styles.dir} ${styles[L.direction === 'long' ? 'long' : L.direction === 'short' ? 'short' : 'watch']}`}>{L.direction === 'long' ? '▲ LONG' : L.direction === 'short' ? '▼ SHORT' : 'WATCH'}</span>
                  <span>{L.setup}</span>
                  {st.key !== 'none' && <span className={`${styles.status} ${styles[`s_${st.key}`]}`}>{st.label}</span>}
                </div>
                <dl className={styles.levels}>
                  <div><dt>Entry</dt><dd>{L.entry_low ? `${formatNumber(L.entry_low)}${L.entry_high && L.entry_high !== L.entry_low ? ` – ${formatNumber(L.entry_high)}` : ''}` : '—'}</dd></div>
                  <div><dt>Stop-loss</dt><dd className={styles.sl}>{L.stop_loss ? formatNumber(L.stop_loss) : '—'}</dd></div>
                  <div><dt>Target 1</dt><dd>{L.target_1 ? formatNumber(L.target_1) : '—'}</dd></div>
                  <div><dt>Target 2</dt><dd>{L.target_2 ? formatNumber(L.target_2) : '—'}</dd></div>
                  <div><dt>Risk : reward</dt><dd>{L.risk_reward ? `1 : ${formatNumber(L.risk_reward, 1)}` : '—'}</dd></div>
                </dl>
                {L.no_trade_reason && <p className={styles.meta}>Why no trade: {L.no_trade_reason}</p>}
                {L.chartink_note && <p className={styles.meta}>📊 {L.chartink_note}</p>}
                <p className={styles.fineBox}>{DISCLAIMER}</p>
              </section>
            )}

            <section aria-label="Score breakdown">
              <h3 className={styles.h3}>Score breakdown</h3>
              <ul className={styles.bars}>
                {d.components.map(c => (
                  <li key={c.label}><span>{c.label}</span><span className={styles.track}><span className={styles.fill} style={{ width: `${Math.max(0, Math.min(100, ((c.points ?? 0) / c.max) * 100))}%` }} /></span>
                    <b>{c.points != null ? formatNumber(c.points, 1) : '—'}</b><span className={styles.pts}>/ {c.max}</span></li>
                ))}
              </ul>
              <p className={styles.meta}>Points each part adds to the final score of {d.score}, out of its maximum.{d.timing ? ` Weights for a filing ${d.timing.toLowerCase()}.` : ''}</p>
            </section>

            <section aria-label="The filing">
              <h3 className={styles.h3}>The filing</h3>
              {d.category && <CategoryChip category={d.category} size="sm" />}
              <p className={styles.headlineBig}>{d.headline}</p>
              {d.summary && <p>{d.summary}</p>}
              {d.analysis && <p className={styles.analysis}>{d.analysis}</p>}
              <p className={styles.links}>
                {d.document_url && <a href={d.document_url} target="_blank" rel="noopener noreferrer">Read the filing (new tab)</a>}
                <Link to={`/stock/${encodeURIComponent(d.symbol)}`}>Open {d.symbol} stock page →</Link>
              </p>
            </section>
          </div>
        )}
      </aside>
    </div>
  )
}
